import React, { useEffect, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import { fetchMonthlyStats } from '../../../services/dishStatsService';
import useEffectiveRestaurantId from '../../../hooks/useEffectiveRestaurantId';
import { printChart } from '../../../utils/printChart';

const MonthlyStatsModal = ({ isOpen, onClose, type }) => {
  const restaurantId = useEffectiveRestaurantId();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);

  const isQR = type === 'qr';
  const title = isQR ? 'Total de Scans QR' : 'Engagements RA';
  const dataKey = isQR ? 'qr' : 'ar';
  const color = isQR ? 'var(--color-primary)' : 'var(--color-accent)';
  const iconName = isQR ? 'QrCode' : 'Scan';

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetchMonthlyStats(restaurantId, 12)
      ?.then(setData)
      ?.catch(() => setData([]))
      ?.finally(() => setLoading(false));
  }, [isOpen, restaurantId]);

  if (!isOpen) return null;

  const handlePrint = () => {
    printChart({
      title,
      subtitle: 'Statistiques mensuelles – 12 derniers mois',
      chartElement: chartRef?.current,
      columns: ['Mois', isQR ? 'Scans' : 'Engagements'],
      rows: (data || [])?.map((d) => [d?.label, d?.[dataKey] || 0]),
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-warm text-sm">
          <p className="font-medium text-foreground mb-1">{label}</p>
          <p style={{ color }} className="font-data font-bold">
            {payload?.[0]?.value?.toLocaleString()} {isQR ? 'scans' : 'engagements'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-card border border-border rounded-xl shadow-warm w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
        onClick={e => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${color}20` }}
            >
              <Icon name={iconName} size={20} color={color} />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground">{title}</h2>
              <p className="text-xs text-muted-foreground font-caption">Statistiques mensuelles – 12 derniers mois</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={loading || !data?.length}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <Icon name="Printer" size={16} />
              Imprimer
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {/* Chart */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
            <Icon name="BarChart2" size={32} className="mb-2 opacity-40" />
            <p className="text-sm">Aucune donnée disponible</p>
          </div>
        ) : (
          <div className="h-64" ref={chartRef}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis
                  dataKey="label"
                  interval={0}
                  tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'inherit' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'inherit' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--color-muted)', opacity: 0.3 }} />
                <Bar dataKey={dataKey} radius={[6, 6, 0, 0]} maxBarSize={40}>
                  {data?.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={index === data?.length - 1 ? color : `${color}70`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Summary row */}
        {!loading && data?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {data?.map((entry, i) => (
              <div key={i} className="text-center">
                <p className="text-xs text-muted-foreground font-caption mb-1">{entry?.label}</p>
                <p
                  className="text-base font-data font-bold"
                  style={{ color: i === data?.length - 1 ? color : 'var(--color-foreground)' }}
                >
                  {(entry?.[dataKey] || 0)?.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyStatsModal;
